import { useCallback, useEffect, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { getEmbarcacaoDimensoes } from "./embarcacoes.gateway";
import { isEmbarcacaoDimensoesEmpty } from "./embarcacoes.types";
import type { EmbarcacaoDimensoesState } from "./embarcacoes.types";

export const DIMENSOES_SESSION_EXPIRED_MESSAGE =
  "Sua sessão expirou. Entre novamente para consultar os detalhes da embarcação.";
export const DIMENSOES_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar os detalhes desta embarcação.";
export const DIMENSOES_NOT_FOUND_MESSAGE = "Embarcação não encontrada.";
export const DIMENSOES_ERROR_MESSAGE =
  "Não foi possível carregar as Dimensões Principais da embarcação.";

const INITIAL_STATE: EmbarcacaoDimensoesState = {
  status: "loading",
  dimensoes: null,
  errorMessage: null,
};

const NOT_FOUND_STATE: EmbarcacaoDimensoesState = {
  status: "notFound",
  dimensoes: null,
  errorMessage: DIMENSOES_NOT_FOUND_MESSAGE,
};

/** A seção existe, mas a fonte não tem nenhum dado a apresentar (AC4). */
const EMPTY_STATE: EmbarcacaoDimensoesState = {
  status: "empty",
  dimensoes: null,
  errorMessage: null,
};

interface UseEmbarcacaoDimensoesResult extends EmbarcacaoDimensoesState {
  reload: () => void;
}

/**
 * Cada falha tem um estado próprio e nenhuma delas devolve campos da seção,
 * para que uma carga incompleta não seja lida como a embarcação consultada.
 */
function toFailure(error: unknown): EmbarcacaoDimensoesState {
  const status = isAPIError(error) ? error.status : null;

  // 401 chega aqui só quando a reautenticação global do HttpClient não corre;
  // ainda assim a sessão expirada é anunciada como tal, e não como falha de carga.
  if (status === 401) {
    return {
      status: "sessionExpired",
      dimensoes: null,
      errorMessage: DIMENSOES_SESSION_EXPIRED_MESSAGE,
    };
  }

  if (status === 403) {
    return {
      status: "forbidden",
      dimensoes: null,
      errorMessage: DIMENSOES_FORBIDDEN_MESSAGE,
    };
  }

  if (status === 404) return NOT_FOUND_STATE;

  return {
    status: "error",
    dimensoes: null,
    errorMessage: getErrorMessage(error) || DIMENSOES_ERROR_MESSAGE,
  };
}

/**
 * Consulta as Dimensões Principais da embarcação pedida. Somente leitura:
 * nenhuma escrita é disparada na abertura nem no fechamento do modal (RN003).
 */
export function useEmbarcacaoDimensoes(id: string): UseEmbarcacaoDimensoesResult {
  const [state, setState] = useState<EmbarcacaoDimensoesState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Fechar o modal ou trocar de embarcação descarta a resposta em trânsito.
    const controller = new AbortController();

    setState(INITIAL_STATE);

    getEmbarcacaoDimensoes({ id, signal: controller.signal })
      .then((dimensoes) => {
        if (cancelled) return;

        if (!dimensoes) {
          setState(NOT_FOUND_STATE);
          return;
        }

        setState(
          isEmbarcacaoDimensoesEmpty(dimensoes)
            ? EMPTY_STATE
            : { status: "success", dimensoes, errorMessage: null },
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState(toFailure(error));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
